/**
 * Synchronous replacement for vue-authorize's v-is-authorized directive.
 *
 * The plugin's directive hid the element, asked the role and permission
 * handlers through promises, and only re-checked when the host component
 * re-rendered. Two things made guarded elements stay hidden:
 *
 * 1. On a fresh browser the login token is stored after the first render, and
 *    nothing forced a re-render of components that had already asked.
 * 2. The user's vault role arrives from the API after the sidebar mounts.
 *
 * This version answers synchronously (all handlers in acl-config.js are
 * synchronous) and watches the store for the token, the role and the current
 * vault, re-applying the check when any of them change. Programmatic callers
 * of this.$authorize.isAuthorized() are untouched.
 *
 * No imports, so the pure check runs under `node --test`.
 */

const toArray = (value) => (value === null || value === undefined ? null : [].concat(value));

/**
 * Same decision the plugin made, without the promises:
 * at least one of the named roles (all roles when none are named) must pass
 * its handler, and when permissions are named the role must hold at least one
 * of them whose handler passes.
 *
 * `ctx` is what the handlers see as `this` (the component instance, which has
 * `$auth`).
 */
export function isAuthorizedSync(acl, roles, permissions, ctx) {
    const roleNames = toArray(roles) ?? Object.keys(acl.roles || {});
    const wanted = toArray(permissions);

    return roleNames.some((name) => {
        const role = acl.roles ? acl.roles[name] : undefined;
        if (!role) {
            return false;
        }

        const handler = typeof role === "function" ? role : role.handler;
        if (typeof handler !== "function" || !handler.call(ctx)) {
            return false;
        }

        if (wanted === null) {
            return true;
        }

        const granted = (typeof role === "function" ? [] : role.permissions) || [];

        return wanted.some((permission) => {
            if (!granted.includes(permission)) {
                return false;
            }
            const check = acl.permissions ? acl.permissions[permission] : undefined;
            return typeof check === "function" && !!check.call(ctx);
        });
    });
}

const STATE_KEY = "__isAuthorized";

const apply = (acl, el) => {
    const entry = el[STATE_KEY];
    if (!entry) {
        return;
    }
    const value = entry.value || {};
    const ok = isAuthorizedSync(acl, value.roles ?? null, value.permissions ?? null, entry.ctx);
    el.style.display = ok ? null : "none";
};

/**
 * Build the Vue 2 directive definition. `store` is the Vuex store or a
 * function returning it. router.js registers the directive while store.js is
 * still loading (store imports router), so it passes a function and the store
 * is only read when the first element binds. The watch covers the fields the
 * role and permission handlers read.
 */
export function createIsAuthorizedDirective(acl, store) {
    const resolveStore = typeof store === "function" ? store : () => store;

    const remember = (el, bindings, vnode) => {
        el[STATE_KEY] = el[STATE_KEY] || {};
        el[STATE_KEY].value = bindings.value;
        el[STATE_KEY].ctx = vnode.context;
    };

    return {
        bind(el, bindings, vnode) {
            remember(el, bindings, vnode);
            el.style.display = "none";
            apply(acl, el);

            const vuex = resolveStore();
            if (vuex && typeof vuex.watch === "function") {
                el[STATE_KEY].unwatch = vuex.watch(
                    (state) => [
                        state.isAuthenticated,
                        state.user && state.user.role,
                        state.user && state.user.data && state.user.data.id,
                        state.vaults && state.vaults.current && state.vaults.current.user_id,
                    ],
                    () => apply(acl, el)
                );
            }
        },

        update(el, bindings, vnode) {
            remember(el, bindings, vnode);
            apply(acl, el);
        },

        componentUpdated(el, bindings, vnode) {
            remember(el, bindings, vnode);
            apply(acl, el);
        },

        unbind(el) {
            const entry = el[STATE_KEY];
            if (entry && typeof entry.unwatch === "function") {
                entry.unwatch();
            }
            delete el[STATE_KEY];
            el.style.display = null;
        },
    };
}
